// /* eslint-disable react-hooks/exhaustive-deps */
// /* eslint-disable no-nested-ternary */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useRef, useEffect, useState, useMemo, useCallback} from 'react';
import styled from 'styled-components';
import {FmlxTooltip} from 'fmlx-common-ui';
// import { Tooltip } from 'react-bootstrap';

import ArrowPoint from './Arrow';
import LabwareBox from './LabwareBox';
import SelectionLine from './SelectionLine';
import Well from './Well';
import {RowLayout} from '../Layout/Layout';
import Spinner from '../Spinner/Spinner';
import DeepWellLabel from '../DeepWellLabel';

import TransformSelectionContainer from '../../contents/ProtocolEditor/TransformSelectionLayer/TransformSelectionContainer';

import {
  IFCPlateVariant,
  Orientation,
  SWIPING_DIRECTION_X,
  SWIPING_DIRECTION_Y,
  WELL_HOVER_INDEX_COLOR_POSITION,
  WELL_HOVER_TYPE,
} from '../../constants/dataEnum';
import {useMouseUpOutside} from '../../lib/customHooks';

import './Labware.scss';
import IFCLabware from './IFCLabware';
import SpearBioLabware from './spearBio/SpearBioLabware';

// #region styled component

const LabwareContainer = styled.div`
  width: 100%;
  height: 100%;

  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently*/
  display: grid;
  grid-template-columns: ${(prop) => `auto 1fr repeat(${prop.col}, 1fr)  1fr auto`};
  grid-template-rows: ${(prop) => `auto 1fr repeat(${prop.row}, 1fr)  1fr auto`};
`;

const LabwareElement = styled.div`
  width: 100%;
  height: 100%;
  grid-area: labware;
  border: ${(prop) => `${prop.isCustomTips ? 'none' : 'solid 0.15em'}`};
  font-size: 0.5em;
  background: ${(prop) => `${prop.isCustomTips ? 'transparent' : 'white'}`};
  position: relative;
  display: grid;
  justify-content: center;
  align-content: center;
  text-align: center;
  grid-column-start: 2;
  grid-row-start: 2;
  grid-column-end: ${(prop) => (prop.col > 8 ? '12' : `${prop.col + 4}`)};
  grid-row-end: ${(prop) => (prop.row > 12 ? '16' : `${prop.row + 4}`)};
  grid-template-columns: ${(prop) => {
    let columnsTemplt;
    if (prop.isCustomTips) {
      columnsTemplt = '1% repeat(8, 1fr) 1%';
    } else if (prop.isIFC) {
      columnsTemplt = `5% repeat(${prop.col}, 1fr) 5%`;
    } else if (prop.col === 1) {
      columnsTemplt = `10% repeat(${prop.col}, 1fr) 10%`;
    } else if (prop.col > 8) {
      columnsTemplt = '1fr repeat(8, 1fr) 1fr';
    } else {
      columnsTemplt = `1fr repeat(${prop.col}, 1fr) 1fr`;
    }

    return columnsTemplt;
  }};
  grid-template-rows: ${(prop) => {
    let rowTemplt;
    if (prop.isCustomTips) {
      rowTemplt = '1% repeat(12, 1fr) 1%';
    } else if (prop.isIFC) {
      rowTemplt = `3.5% repeat(${prop.row}, 1fr) 3.5%`;
    } else if (prop.row === 1) {
      rowTemplt = `7% repeat(${prop.row}, 1fr) 7%`;
    } else if (prop.row > 12) {
      rowTemplt = '1fr repeat(12, 1fr) 1fr';
    } else {
      rowTemplt = `1fr repeat(${prop.row}, 1fr) 1fr`;
    }
    return rowTemplt;
  }};
  z-index: 1;
`;

const LabwareWellContainer = styled.div`
  grid-column-start: 2;
  grid-row-start: 2;
  grid-column-end: ${(prop) => (prop.col > 8 ? '10' : `${prop.col + 2}`)};
  grid-row-end: ${(prop) => (prop.row > 12 ? '14' : `${prop.row + 2}`)};
  display: grid;
  grid-template-columns: repeat(${(prop) => prop.col}, 1fr);
  grid-template-rows: repeat(${(prop) => prop.row}, 1fr);
  position: relative;
  background: ${(prop) => `${prop.isCustomTips ? 'transparent' : 'white'}`};
  width: 100%;
  height: 100%;
`;

const RightLabelContainer = styled.div`
  grid-area: right-label-container;
  display: grid;

  grid-auto-flow: row;
  grid-auto-rows: 1fr;
  justify-content: center;
  align-items: center;
  vertical-align: middle;
  text-align: center;
  grid-column: ${(prop) => (prop.row > 12 ? '14/14' : `${prop.row + 2}/${prop.row + 2}`)};
  grid-row: 2/-2;
`;

const RightLabelPotraitContainer = styled.div`
  grid-area: right-label-container;
  display: grid;

  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  justify-content: center;
  align-items: center;
  vertical-align: middle;
  text-align: center;
  grid-column: 2/-2;
  grid-row: ${(prop) => (prop.row > 12 ? '14/14' : `${prop.row + 2}/${prop.row + 2}`)};
`;

// #endregion styled component

function Labware({
  ID,
  rowCount,
  colCount,
  isWellRectangle,
  orientation,
  swipeEnable,
  oneDimensionalSwipeOnly,
  wellStyle,
  onlyAllowTap,
  showLeftUpperArrow,
  showUpperArrow,
  showBottomArrow,
  showLeftArrow,
  showRightArrow,
  showSelectionLine,
  showColIdx,
  showRowIdx,
  onArrowClick,
  onSwipeStart,
  onSwipeMove,
  onSwipeEnd,
  onHoverWell,
  onLeaveWell,
  onClickWell,
  onLoaded,
  zoom,
  isCustomTips,
  transformSelectionTasks,
  isHoveredLabware,
  wellHoverMode,
  wellHoverHighlightPosition,
  initHoveredWell,
  showLabwareName,
  labwareName,
  isDeepWell,
  showTS,
  layouts,
  onHoverTransformSelection,
  onDragTransformSelection,
  onDropTransformSelection,
  labwareIndexes,
  isIFC,
  IFCvariant,
  disabledQuadrant,
  isSpearBio,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredWell, setHoveredWell] = useState(initHoveredWell);

  const labwareContainerRef = useRef(null);
  const swipeEnableRef = useRef(swipeEnable);
  const swipeStartingWellRef = useRef(null);
  const isMouseDownRef = useRef(false);
  const swipingDirectionYRef = useRef(null);
  const swipingDirectionXRef = useRef(null);
  const swipeLatestEndWellRef = useRef(null);
  const labwareWellContainerRef = useRef(null);

  let doesWellStyleHaveIndex = true;

  const ratio = {
    width: 10,
    height: 7,
  };

  const isPotrait = orientation === Orientation.POTRAIT;
  let col = colCount;
  let row = rowCount;

  useEffect(() => {
    setHoveredWell(initHoveredWell);
  }, [initHoveredWell]);

  if (isPotrait) {
    const temp = ratio.width;
    ratio.width = ratio.height;
    ratio.height = temp;

    col = rowCount;
    row = colCount;
  }

  const handleMouseUp = (evt) => {
    if (!isMouseDownRef.current) {
      return;
    }

    const selectionLine = document.querySelector('.selectionLine');

    if (selectionLine) {
      selectionLine.style.height = '0px';
      selectionLine.style.width = '0px';
      selectionLine.classList.remove('show');
    }

    if (swipeStartingWellRef.current && swipeLatestEndWellRef.current) {
      const wellID = swipeLatestEndWellRef.current;

      if (oneDimensionalSwipeOnly) {
        let targetWellId;

        if (
          swipingDirectionYRef.current === SWIPING_DIRECTION_Y.UP ||
          swipingDirectionYRef.current === SWIPING_DIRECTION_Y.DOWN
        ) {
          targetWellId = `${swipeStartingWellRef?.current?.id.split('-')[0]}-${
            wellID.split('-')[1]
          }-${wellID.split('-')[2]}`;
        } else {
          targetWellId = wellID;
        }

        onSwipeEnd(targetWellId, swipeStartingWellRef.current.id, evt?.ctrlKey, evt?.shiftKey);
      } else {
        onSwipeEnd(wellID, swipeStartingWellRef.current.id, evt?.ctrlKey, evt?.shiftKey);
      }

      swipeLatestEndWellRef.current = null;
    }

    isMouseDownRef.current = false;
    swipeStartingWellRef.current = null;
  };

  useMouseUpOutside(labwareWellContainerRef, () => {
    if (isCustomTips) {
      onSwipeEnd();
    } else {
      handleMouseUp();
    }
  });

  function resizeLabware() {
    const labwareElement = labwareContainerRef.current;

    if (!labwareElement) {
      return;
    }

    const {width} = labwareElement.getBoundingClientRect();
    const newHeight = (width / ratio.width) * ratio.height;

    labwareElement.style.height = newHeight;
  }

  useEffect(() => {
    if (!isLoaded) {
      onLoaded();
    }

    setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resizeLabware();
    window.addEventListener('resize', resizeLabware);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, labwareContainerRef, isHoveredLabware]);

  useEffect(() => {
    swipeEnableRef.current = swipeEnable;
  }, [swipeEnable]);

  const onShowSelectionLine = useCallback(
    (wellId, isFirstClick = false) => {
      swipingDirectionYRef.current = null;
      swipingDirectionXRef.current = null;

      if (!swipeEnableRef?.current || (!isMouseDownRef.current && !isFirstClick)) {
        return;
      }

      const selectionLine = document.querySelector('.selectionLine');

      if (!selectionLine) {
        return;
      }

      const well = document.getElementById(`${wellId}-${ID}`);

      const wellBound = well.getBoundingClientRect();
      const {top: wellTop, left: wellLeft, height: wellHeight, width: wellWidth} = wellBound;

      const wellY = wellTop + wellHeight / 2;
      const wellX = wellLeft + wellWidth / 2;

      if (isFirstClick || onlyAllowTap) {
        swipeStartingWellRef.current = well;
        swipeLatestEndWellRef.current = wellId;

        if (showSelectionLine) {
          selectionLine.classList.add('show');
        }

        selectionLine.style.top = `${wellY}px`;
        selectionLine.style.left = `${wellX}px`;

        return;
      }

      if (!swipeStartingWellRef?.current) {
        return;
      }

      const swipeStartingWellBound = swipeStartingWellRef.current.getBoundingClientRect();
      const {top: startingWellTop, left: startingWellLeft} = swipeStartingWellBound;

      const startingWellY = startingWellTop + wellHeight / 2;
      const startingWellX = startingWellLeft + wellWidth / 2;

      const lineHeight = wellY - startingWellY;
      const lineWidth = wellX - startingWellX;

      selectionLine.style.top = `${startingWellY}px`;
      selectionLine.style.left = `${startingWellX}px`;
      swipingDirectionYRef.current = null;
      swipingDirectionXRef.current = null;

      if (lineHeight < 0) {
        selectionLine.style.top = `${startingWellY + lineHeight}px`;
        swipingDirectionYRef.current = SWIPING_DIRECTION_Y.UP;

        if (lineWidth < 0) {
          selectionLine.style.left = `${startingWellX + lineWidth}px`;
          swipingDirectionXRef.current = SWIPING_DIRECTION_X.LEFT;
        } else if (lineWidth > 0) {
          selectionLine.style.left = `${startingWellX}px`;
          swipingDirectionXRef.current = SWIPING_DIRECTION_X.RIGHT;
        }

        if (oneDimensionalSwipeOnly) {
          selectionLine.style.left = `${startingWellX}px`;
          swipingDirectionXRef.current = null;
        }
      } else if (lineHeight > 0) {
        selectionLine.style.top = `${startingWellY}px`;
        swipingDirectionYRef.current = SWIPING_DIRECTION_Y.DOWN;

        if (lineWidth < 0) {
          selectionLine.style.left = `${startingWellX + lineWidth}px`;
          swipingDirectionXRef.current = SWIPING_DIRECTION_X.LEFT;
        } else if (lineWidth > 0) {
          selectionLine.style.left = `${startingWellX}px`;
          swipingDirectionXRef.current = SWIPING_DIRECTION_X.RIGHT;
        }

        if (oneDimensionalSwipeOnly) {
          selectionLine.style.left = `${startingWellX}px`;
          swipingDirectionXRef.current = null;
        }
      } else if (lineWidth < 0) {
        selectionLine.style.left = `${startingWellX + lineWidth}px`;
        swipingDirectionXRef.current = SWIPING_DIRECTION_X.LEFT;

        if (lineHeight < 0) {
          swipingDirectionYRef.current = SWIPING_DIRECTION_Y.UP;
        } else if (lineHeight > 0) {
          swipingDirectionYRef.current = SWIPING_DIRECTION_Y.DOWN;
        }

        if (oneDimensionalSwipeOnly) {
          swipingDirectionYRef.current = null;
        }
      } else if (lineWidth > 0) {
        swipingDirectionXRef.current = SWIPING_DIRECTION_X.RIGHT;

        if (lineHeight < 0) {
          swipingDirectionYRef.current = SWIPING_DIRECTION_Y.UP;
        } else if (lineHeight > 0) {
          swipingDirectionYRef.current = SWIPING_DIRECTION_Y.DOWN;
        }

        if (oneDimensionalSwipeOnly) {
          swipingDirectionYRef.current = null;
        }
      }

      if (oneDimensionalSwipeOnly) {
        if (Math.abs(lineHeight) > 0) {
          selectionLine.style.height = `${Math.abs(lineHeight)}px`;
          selectionLine.style.width = 0;
        } else {
          selectionLine.style.width = `${Math.abs(lineWidth)}px`;
          selectionLine.style.height = 0;
        }
      } else {
        selectionLine.style.height = `${Math.abs(lineHeight)}px`;
        selectionLine.style.width = `${Math.abs(lineWidth)}px`;
      }
    },
    [ID, oneDimensionalSwipeOnly, onlyAllowTap, showSelectionLine],
  );

  const onHideSelectionLine = () => {
    const selectionLine = document.querySelector('.selectionLine');

    if (selectionLine && !isCustomTips) {
      // isMouseDownRef.current = false;
      // swipeStartingWellRef.current = null;

      selectionLine.style.height = '0px';
      selectionLine.style.width = '0px';
      selectionLine.classList.remove('show');
    }
  };

  const highlightWell = (wellID) => {
    const [_row, _column, _index] = wellID.split('-');
    setHoveredWell({
      column: parseInt(_column, 10),
      row: parseInt(_row, 10),
      index: parseInt(_index, 10),
    });
  };

  const wellEvents = useMemo(
    () => ({
      onMouseLeave: (evt, wellID) => {
        evt.preventDefault();
        setHoveredWell({column: null, row: null, index: null});
        onLeaveWell(wellID);
      },
      onClickWell: (evt, wellID) => {
        evt.preventDefault();
        onClickWell(wellID);
      },
      onMouseDown: (evt, wellID) => {
        evt.preventDefault();
        isMouseDownRef.current = true;
        onSwipeStart(wellID, evt.ctrlKey, evt.shiftKey);
        onShowSelectionLine(wellID, true);
      },
      onMouseEnter: (evt, wellID) => {
        evt.preventDefault();

        swipeLatestEndWellRef.current = wellID;

        highlightWell(wellID);
        onHoverWell(wellID, evt.ctrlKey, evt.shiftKey);
        onShowSelectionLine(wellID);
        onSwipeMove(wellID, swipingDirectionYRef.current, swipingDirectionXRef.current, evt.ctrlKey, evt.shiftKey);
      },
    }),
    [onClickWell, onHoverWell, onLeaveWell, onShowSelectionLine, onSwipeMove, onSwipeStart],
  );

  const generateWells = (rows, cols, index = 0) => {
    const wellElements = [];

    const wellStyleArr = Object.keys(wellStyle);
    if (wellStyleArr.length !== 0) {
      const wellIndex = wellStyleArr[0].split('-');
      if (!wellIndex[2]) {
        doesWellStyleHaveIndex = false;
      }
    }

    for (let iRow = 0; iRow < rows; iRow += 1) {
      let charIndex;

      for (let iCol = 0; iCol < cols; iCol += 1) {
        const newRow = isPotrait ? cols - iCol : iRow + 1;
        const newCol = isPotrait ? iRow + 1 : iCol + 1;
        let wellID = `${newRow}-${newCol}-${index}`;

        if (!doesWellStyleHaveIndex) {
          const wellIDWithoutIndex = wellID.split('-');
          wellID = `${wellIDWithoutIndex[0]}-${wellIDWithoutIndex[1]}`;
        }

        const style = wellStyle[wellID];
        let wellText = '';
        if (isPotrait) {
          charIndex = String.fromCharCode(64 + cols - iCol);
          wellText =
            hoveredWell.column === iRow + 1 && hoveredWell.row === cols - iCol && hoveredWell.index === index
              ? `${charIndex}${iRow + 1}`
              : '';
        } else {
          charIndex = String.fromCharCode(65 + iRow);
          wellText =
            hoveredWell.column === iCol + 1 && hoveredWell.row === iRow + 1 && hoveredWell.index === index
              ? `${charIndex}${iCol + 1}`
              : '';
        }

        let disabledWell = false;

        // disable quadrant 1
        if (disabledQuadrant.includes(1) && newRow % 2 === 1 && newCol % 2 === 1) {
          disabledWell = true;
        }

        // disable quadrant 2
        if (disabledQuadrant.includes(2) && newRow % 2 === 1 && newCol % 2 === 0) {
          disabledWell = true;
        }

        // disable quadrant 3
        if (disabledQuadrant.includes(3) && newRow % 2 === 0 && newCol % 2 === 0) {
          disabledWell = true;
        }

        // disable quadrant 4
        if (disabledQuadrant.includes(4) && newRow % 2 === 0 && newCol % 2 === 1) {
          disabledWell = true;
        }

        wellElements.push(
          <Well
            key={`${wellID}-${ID}`}
            id={`${wellID}-${ID}`}
            isWellRectangle={isWellRectangle}
            isWellTrough={rowCount === 1 || colCount === 1}
            background={style?.background}
            color={style?.color}
            border={style?.border}
            borderSize={style?.borderSize}
            containerColor={style?.containerColor}
            opacity={style?.opacity}
            onMouseLeave={(evt) => wellEvents.onMouseLeave(evt, wellID)}
            onClickWell={(evt) => wellEvents.onClickWell(evt, wellID)}
            onMouseDown={(evt) => wellEvents.onMouseDown(evt, wellID)}
            onMouseEnter={(evt) => wellEvents.onMouseEnter(evt, wellID)}
            onTouchStart={(evt) => wellEvents.onMouseDown(evt, wellID)}
            wellText={wellText}
            showWellText={wellHoverMode === WELL_HOVER_TYPE.TEXT || wellHoverMode === WELL_HOVER_TYPE.BOTH}
            disabled={disabledWell}
          />,
        );
      }
    }

    return wellElements;
  };

  const generateUpperIndexes = () => {
    const upperIndex = [];

    for (let index = 0; index < colCount; index += 1) {
      if (showColIdx) {
        upperIndex.push(
          <div
            key={index}
            className={`
              index
              ${colCount > 12 ? 'index-small' : 'index-medium'}  
              upper-index 
              ${zoom || isHoveredLabware ? 'zoom-index' : ''} 
              ${
                hoveredWell.column === index + 1 &&
                (wellHoverMode === WELL_HOVER_TYPE.INDEX_COLOR || wellHoverMode === WELL_HOVER_TYPE.BOTH) &&
                (wellHoverHighlightPosition === WELL_HOVER_INDEX_COLOR_POSITION.TOP ||
                  wellHoverHighlightPosition === WELL_HOVER_INDEX_COLOR_POSITION.BOTH)
                  ? 'hovered'
                  : ''
              }`}
          >
            <p>{index + 1}</p>
          </div>,
        );
      }
    }

    return upperIndex;
  };

  const generateLeftIndexes = () => {
    const leftIndex = [];

    for (let index = rowCount; index > 0; index -= 1) {
      const char = isPotrait ? String.fromCharCode(64 + index) : String.fromCharCode(65 + rowCount - index);

      if (showRowIdx) {
        leftIndex.push(
          <div
            key={index}
            className={`
              index
              ${rowCount > 8 ? 'index-small' : 'index-medium'} 
              left-index 
              ${zoom || isHoveredLabware ? 'zoom-index' : ''} 
              ${
                hoveredWell.row === index &&
                (wellHoverMode === WELL_HOVER_TYPE.INDEX_COLOR || wellHoverMode === WELL_HOVER_TYPE.BOTH) &&
                (wellHoverHighlightPosition === WELL_HOVER_INDEX_COLOR_POSITION.LEFT ||
                  wellHoverHighlightPosition === WELL_HOVER_INDEX_COLOR_POSITION.BOTH)
                  ? 'hovered'
                  : ''
              }`}
          >
            <p>{char}</p>
          </div>,
        );
      }
    }

    return leftIndex;
  };

  const generateUpperArrows = () => {
    const upperArrows = [];
    const isOnlyShowSeveralUpperArrow = Array.isArray(showUpperArrow);

    for (let index = 1; index <= colCount; index += 1) {
      if (isOnlyShowSeveralUpperArrow) {
        if (!showUpperArrow.includes(index)) {
          upperArrows.push(<div />);
        }
      }

      upperArrows.push(
        <div className="upper-arrow">
          <ArrowPoint arrowKey={`${index}`} direction={isPotrait ? 'left' : 'down'} onClick={onArrowClick} />
        </div>,
      );
    }

    return upperArrows;
  };

  const generateBottomArrows = () => {
    const bottomArrows = [];
    const isOnlyShowSeveralBottomArrow = Array.isArray(showBottomArrow);

    for (
      let index = isPotrait ? colCount - 1 : 1;
      isPotrait ? index >= 0 : index <= colCount;
      isPotrait ? (index -= 1) : (index += 1)
    ) {
      if (isOnlyShowSeveralBottomArrow) {
        if (!showBottomArrow.includes(index)) {
          bottomArrows.push(<div />);
        }
      }

      bottomArrows.push(
        <ArrowPoint
          arrowKey={`${isPotrait ? colCount - index : index}-2`}
          direction={isPotrait ? 'right' : 'up'}
          onClick={onArrowClick}
        />,
      );
    }

    return bottomArrows;
  };

  const generateLeftArrows = () => {
    const leftArrows = [];
    const isOnlyShowSeveralLeftArrow = Array.isArray(showLeftArrow);

    for (
      let index = isPotrait ? rowCount : 1;
      isPotrait ? index > 0 : index <= rowCount;
      isPotrait ? (index -= 1) : (index += 1)
    ) {
      if (isOnlyShowSeveralLeftArrow) {
        if (!showLeftArrow.includes(index)) {
          leftArrows.push(<div />);
        }
      }

      leftArrows.push(
        <ArrowPoint
          arrowKey={`${String.fromCharCode(64 + index)}`}
          direction={isPotrait ? 'down' : 'right'}
          onClick={onArrowClick}
        />,
      );
    }

    return leftArrows;
  };

  const generateRightArrows = () => {
    const rightArrows = [];
    const isOnlyShowSeveralRightArrow = Array.isArray(showRightArrow);

    for (
      let index = isPotrait ? rowCount : 1;
      isPotrait ? index > 0 : index <= rowCount;
      isPotrait ? (index -= 1) : (index += 1)
    ) {
      if (isOnlyShowSeveralRightArrow) {
        if (!showRightArrow.includes(index)) {
          rightArrows.push(<div />);
        }
      }

      rightArrows.push(
        <ArrowPoint
          arrowKey={`${String.fromCharCode(64 + index)}-B`}
          direction={isPotrait ? 'up' : 'left'}
          onClick={onArrowClick}
        />,
      );
    }

    return rightArrows;
  };

  if (!isLoaded) {
    return (
      <RowLayout alignItems="center" justifyContent="center">
        <Spinner />
      </RowLayout>
    );
  }

  const renderWells = () => {
    if (isIFC) {
      return (
        <IFCLabware
          generateWell={(r, c, idx) => generateWells(r, c, idx)}
          labwareIndexes={labwareIndexes}
          IFCvariant={IFCvariant}
          zoom={zoom || isHoveredLabware}
        />
      );
    }
    if (isSpearBio) {
      return (
        <SpearBioLabware
          ID={ID}
          rowCount={rowCount}
          colCount={colCount}
          wellStyle={wellStyle}
          wellHoverMode={wellHoverMode}
          hoveredWell={hoveredWell}
          wellEventProps={wellEvents}
        />
      );
    }

    return generateWells(row, col);
  };

  return (
    <LabwareContainer ref={labwareContainerRef} row={row} col={col} isPotrait={isPotrait} ratio={ratio}>
      <LabwareBox
        itemID={ID}
        style={{
          gridArea: 'labware',
          gridColumnStart: '2',
          gridRowStart: '2',
          gridColumnEnd: `${col + 4}`,
          gridRowEnd: `${row + 4}`,
          background: `${isCustomTips ? 'unset' : '#5e5e5e'}`,
        }}
        orientation={orientation}
        isCustomTips={isCustomTips}
      >
        <LabwareElement
          row={row}
          col={col}
          isPotrait={isPotrait}
          isCustomTips={isCustomTips}
          id="labware"
          className="labware"
          onTouchMove={(event) => {
            event.preventDefault();
            const myLocation = event.changedTouches[0];
            const el = document.elementFromPoint(myLocation.clientX, myLocation.clientY);

            if (el?.id) {
              onShowSelectionLine(el.id);
              onSwipeMove(el.id, swipingDirectionYRef.current, swipingDirectionXRef.current);
            }
          }}
          onTouchEnd={(event) => {
            event.preventDefault();
            const myLocation = event.changedTouches[0];
            const el = document.elementFromPoint(myLocation.clientX, myLocation.clientY);

            if (el?.id) {
              onSwipeEnd(el.id, swipeStartingWellRef?.current?.id);
              onHideSelectionLine();
            }
          }}
          onMouseLeave={(evt) => {
            evt.preventDefault();

            onHideSelectionLine();
          }}
          onMouseUp={(evt) => {
            evt.preventDefault();

            handleMouseUp(evt);
          }}
          isIFC={isIFC}
        >
          <>
            {showColIdx && (
              <div className={`upper-index-container ${isPotrait && 'potrait'}`}>{generateUpperIndexes()}</div>
            )}
            {showRowIdx && (
              <div className={`left-index-container  ${isPotrait && 'potrait'}`}>{generateLeftIndexes()}</div>
            )}
          </>
          <LabwareWellContainer
            ref={labwareWellContainerRef}
            row={row}
            col={col}
            isCustomTips
            // eslint-disable-next-line no-nested-ternary
            style={{gap: `${rowCount === 1 || colCount === 1 ? '2%' : isCustomTips ? '2px' : 0}`}}
            isSpearBio={isSpearBio}
          >
            {renderWells()}
          </LabwareWellContainer>
          {showLabwareName && isPotrait && (
            <RightLabelPotraitContainer row={row} col={col} className="right-label-container potrait">
              <div
                className={`label 
                  ${zoom ? 'label-zoom' : ''}`}
              >
                <FmlxTooltip placement="top" title={labwareName}>
                  <p
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      padding: zoom ? '0px 40px' : '0px 30px',
                      // eslint-disable-next-line no-nested-ternary
                      fontSize: zoom && isIFC ? 9 : !zoom && isIFC ? 8 : 'inherit',
                    }}
                  >
                    {labwareName}
                  </p>
                </FmlxTooltip>
                {isDeepWell ? (
                  <div className="deep-well-label">
                    <DeepWellLabel />
                  </div>
                ) : (
                  ''
                )}
              </div>
            </RightLabelPotraitContainer>
          )}
          {showLabwareName && !isPotrait && (
            <RightLabelContainer row={row} col={col} className="right-label-container">
              <div
                className={`label 
                  ${zoom ? 'label-zoom' : ''}`}
              >
                <FmlxTooltip placement="top" title={labwareName}>
                  <p
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      padding: zoom ? '0px 40px' : '0px 30px',
                      // eslint-disable-next-line no-nested-ternary
                      fontSize: zoom && isIFC ? 9 : !zoom && isIFC ? 6 : 'inherit',
                    }}
                  >
                    {labwareName}
                  </p>
                </FmlxTooltip>
                {isDeepWell ? (
                  <div className="deep-well-label">
                    <DeepWellLabel />
                  </div>
                ) : (
                  ''
                )}
              </div>
            </RightLabelContainer>
          )}
          {!isCustomTips && showTS && (
            <TransformSelectionContainer
              itemID={ID}
              row={col}
              col={row}
              tasks={transformSelectionTasks}
              layouts={layouts}
              onHoverTransformSelection={onHoverTransformSelection}
              onDragTransformSelection={onDragTransformSelection}
              onDropTransformSelection={onDropTransformSelection}
              setHoveredWell={setHoveredWell}
              hoveredWell={hoveredWell}
            />
          )}
        </LabwareElement>
      </LabwareBox>
      {showLeftUpperArrow && (
        <div className={`arrowContainer left-upper-arrow ${isPotrait && 'potrait'}`}>
          <ArrowPoint arrowKey="all" direction={`${isPotrait ? 'down-left' : 'down-right'}`} onClick={onArrowClick} />
        </div>
      )}
      {showUpperArrow && (
        <div className={`arrowContainer upper ${isPotrait && 'potrait'}`}>{generateUpperArrows()}</div>
      )}
      {showLeftArrow && <div className={`arrowContainer left ${isPotrait && 'potrait'}`}>{generateLeftArrows()}</div>}
      {showRightArrow && (
        <div className={`arrowContainer right ${isPotrait && 'potrait'}`}>{generateRightArrows()}</div>
      )}
      {showBottomArrow && (
        <div className={`arrowContainer bottom ${isPotrait && 'potrait'}`}>{generateBottomArrows()}</div>
      )}
      <SelectionLine />
    </LabwareContainer>
  );
}

function areEqual(prevProps, nextProps) {
  return (
    prevProps.ID === nextProps.ID &&
    prevProps.rowCount === nextProps.rowCount &&
    prevProps.colCount === nextProps.colCount &&
    prevProps.orientation === nextProps.orientation &&
    prevProps.swipeEnable === nextProps.swipeEnable &&
    prevProps.oneDimensionalSwipeOnly === nextProps.oneDimensionalSwipeOnly &&
    prevProps.isWellRectangle === nextProps.isWellRectangle &&
    prevProps.wellHoverMode === nextProps.wellHoverMode &&
    _.isEqual(prevProps.showLeftArrow, nextProps.showLeftArrow) &&
    _.isEqual(prevProps.showUpperArrow, nextProps.showUpperArrow) &&
    _.isEqual(prevProps.showBottomArrow, nextProps.showBottomArrow) &&
    _.isEqual(prevProps.showLeftUpperArrow, nextProps.showLeftUpperArrow) &&
    _.isEqual(prevProps.showRightArrow, nextProps.showRightArrow) &&
    _.isEqual(prevProps.showRowIdx, nextProps.showRowIdx) &&
    _.isEqual(prevProps.showColIdx, nextProps.showColIdx) &&
    _.isEqual(prevProps.wellStyle, nextProps.wellStyle) &&
    _.isEqual(prevProps.isHoveredLabware, nextProps.isHoveredLabware) &&
    _.isEqual(prevProps.zoom, nextProps.zoom) &&
    _.isEqual(prevProps.initHoveredWell, nextProps.initHoveredWell) &&
    _.isEqual(prevProps.showTS, nextProps.showTS) &&
    _.isEqual(prevProps.layouts, nextProps.layouts) &&
    _.isEqual(prevProps.onHoverTransformSelection, nextProps.onHoverTransformSelection) &&
    _.isEqual(prevProps.onDragTransformSelection, nextProps.onDragTransformSelection) &&
    _.isEqual(prevProps.onDropTransformSelection, nextProps.onDropTransformSelection) &&
    _.isEqual(prevProps.onClickWell, nextProps.onClickWell)
  );
}

Labware.propTypes = {
  ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  rowCount: PropTypes.number.isRequired,
  colCount: PropTypes.number.isRequired,
  isWellRectangle: PropTypes.bool.isRequired,
  isIFC: PropTypes.bool,

  orientation: PropTypes.string,
  swipeEnable: PropTypes.bool,
  oneDimensionalSwipeOnly: PropTypes.bool,
  wellStyle: PropTypes.object,
  onlyAllowTap: PropTypes.bool,
  showLeftUpperArrow: PropTypes.bool,
  showSelectionLine: PropTypes.bool,
  zoom: PropTypes.bool,
  isCustomTips: PropTypes.bool,

  showLabwareName: PropTypes.bool,
  labwareName: PropTypes.string,
  isDeepWell: PropTypes.bool,
  showTS: PropTypes.bool,

  showUpperArrow: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  showBottomArrow: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  showLeftArrow: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  showRightArrow: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  showRowIdx: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  showColIdx: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  wellHoverMode: PropTypes.oneOf(Object.values(WELL_HOVER_TYPE)),
  wellHoverHighlightPosition: PropTypes.oneOf(Object.values(WELL_HOVER_INDEX_COLOR_POSITION)),

  onArrowClick: PropTypes.func,
  onSwipeStart: PropTypes.func,
  onSwipeMove: PropTypes.func,
  onSwipeEnd: PropTypes.func,
  onHoverWell: PropTypes.func,
  onLeaveWell: PropTypes.func,
  onClickWell: PropTypes.func,

  onHoverTransformSelection: PropTypes.func,
  onDragTransformSelection: PropTypes.func,
  onDropTransformSelection: PropTypes.func,

  onLoaded: PropTypes.func,

  layouts: PropTypes.object,
  transformSelectionTasks: PropTypes.array,
  isHoveredLabware: PropTypes.any,

  initHoveredWell: PropTypes.object,
  labwareIndexes: PropTypes.object,
  IFCvariant: PropTypes.number,
  disabledQuadrant: PropTypes.array,
  isSpearBio: PropTypes.bool,
};

Labware.defaultProps = {
  isIFC: false,
  orientation: Orientation.LANDSCAPE,
  swipeEnable: false,
  oneDimensionalSwipeOnly: false,
  wellStyle: {},
  onlyAllowTap: false,
  showLeftUpperArrow: false,
  showSelectionLine: true,

  showUpperArrow: false,
  showBottomArrow: false,
  showLeftArrow: false,
  showRightArrow: false,
  showRowIdx: false,
  showColIdx: false,
  zoom: false,
  isCustomTips: false,

  layouts: {},
  transformSelectionTasks: [],
  isHoveredLabware: null,
  wellHoverMode: WELL_HOVER_TYPE.BOTH,
  wellHoverHighlightPosition: WELL_HOVER_INDEX_COLOR_POSITION.BOTH,
  initHoveredWell: {row: null, column: null},
  labwareIndexes: {},

  showLabwareName: false,
  labwareName: '',
  isDeepWell: false,
  showTS: false,
  IFCvariant: IFCPlateVariant.IFC96,
  disabledQuadrant: [],
  isSpearBio: false,

  onArrowClick: () => {},
  onSwipeStart: () => {},
  onSwipeMove: () => {},
  onSwipeEnd: () => {},
  onHoverWell: () => {},
  onLeaveWell: () => {},
  onClickWell: () => {},

  onHoverTransformSelection: () => {},
  onDragTransformSelection: () => {},
  onDropTransformSelection: () => {},

  onLoaded: () => {},
};

export const WELL_HOVER_MODE = WELL_HOVER_TYPE;

export default React.memo(Labware, areEqual);
